<template>
  <div class="landing-page">
    <!-- Loading State -->
    <div v-if="restaurantLoading" class="loading-state">
      <div class="spinner-lg"></div>
      <p>{{ $t('loadingRestaurant') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="restaurantError" class="error-state">
      <div class="icon">❌</div>
      <h2>{{ $t('restaurantNotFound') }}</h2>
      <p>{{ restaurantError }}</p>
    </div>

    <!-- Main Content -->
    <template v-else>
      <div class="hero-section">
        <div class="logo-container">
          <img 
            v-if="restaurantStore.restaurantLogo" 
            :src="restaurantStore.restaurantLogo" 
            :alt="restaurantStore.restaurantName"
            class="restaurant-logo"
          />
          <div v-else class="logo-emoji">🍔</div> 
        </div>
        <h1>{{ restaurantStore.restaurantName }}</h1>
        <p class="subtitle">{{ $t('tableOrdering') }}</p>
      </div>

      <div class="card-container slide-up-enter-active">
        <div class="tabs">
          <button 
            class="tab-btn" 
            :class="{ active: activeTab === 'start' }" 
            @click="activeTab = 'start'"
          >
            {{ $t('startTable') }}
          </button>
          <button 
            class="tab-btn" 
            :class="{ active: activeTab === 'join' }" 
            @click="activeTab = 'join'"
          >
            {{ $t('joinTable') }}
          </button>
          <div class="tab-indicator" :style="{ transform: activeTab === 'start' ? 'translateX(0)' : 'translateX(100%)' }"></div>
        </div>

        <div class="content-area">
          <transition name="fade" mode="out-in">
            <!-- Start Order Section -->
            <div v-if="activeTab === 'start'" key="start" class="form-container">
              <div class="input-group">
                <label>{{ $t('tableNumber') }}</label>
                <input 
                  v-model="tableNumber" 
                  type="number" 
                  :placeholder="$t('enterTableNumber')" 
                  class="input-field"
                  :disabled="tableFromQr"
                  @keyup.enter="handleStart"
                />
                <p v-if="tableFromQr" class="hint table-hint">
                  ✓ {{ $t('tableFromQr') }}
                </p>
              </div>

              <div class="mode-toggle-container">
                <div class="mode-toggle" @click="isCollaborativeStart = !isCollaborativeStart">
                  <div class="toggle-track" :class="{ active: isCollaborativeStart }">
                    <div class="toggle-thumb"></div>
                  </div>
                  <span class="toggle-label">{{ $t('groupOrdering') }}</span>
                </div>
              </div>

              <transition name="slide-up">
                <div v-if="isCollaborativeStart" class="input-group">
                  <label>{{ $t('yourName') }}</label>
                  <input 
                    v-model="userName" 
                    type="text" 
                    :placeholder="$t('enterYourName')" 
                    class="input-field"
                    @keyup.enter="handleStart"
                  />
                  <p class="hint">{{ $t('hostHint') }}</p>
                </div>
              </transition>

              <button 
                @click="handleStart" 
                class="btn-primary"
                :disabled="!isValidStart || loading"
              >
                <span v-if="loading" class="spinner"></span>
                <span v-else>{{ isCollaborativeStart ? $t('createSession') : $t('startOrdering') }}</span>
              </button>
            </div>

            <!-- Join Order Section -->
            <div v-else key="join" class="form-container">
              <div class="input-group">
                <label>{{ $t('pinCode') }}</label>
                <div class="pin-input-wrapper">
                  <input 
                    v-model="pinCode" 
                    type="text" 
                    placeholder="0000" 
                    class="input-field pin-field"
                    maxlength="4"
                    @keyup.enter="handleJoin"
                  />
                </div>
              </div>

              <div class="input-group">
                <label>{{ $t('yourName') }}</label>
                <input 
                  v-model="userName" 
                  type="text" 
                  :placeholder="$t('enterYourName')" 
                  class="input-field"
                  @keyup.enter="handleJoin"
                />
              </div>

              <button 
                @click="handleJoin" 
                class="btn-primary"
                :disabled="!isValidJoin || loading"
              >
                <span v-if="loading" class="spinner"></span>
                <span v-else>{{ $t('joinSession') }}</span>
              </button>
              
              <p v-if="error" class="error-message">{{ error }}</p>
            </div>
          </transition>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { useRestaurantStore } from '@/stores/restaurant'

const props = defineProps({
  restaurantSlug: {
    type: String,
    required: true
  }
})

const router = useRouter()
const route = useRoute()
const sessionStore = useSessionStore()
const restaurantStore = useRestaurantStore()

const activeTab = ref('start')
const tableNumber = ref('')
const userName = ref('')
const pinCode = ref('')
const isCollaborativeStart = ref(false)
const loading = ref(false)
const error = ref('')
const restaurantLoading = ref(true)
const restaurantError = ref('')
const tableFromQr = ref(false)

// Load restaurant data on mount
onMounted(async () => {
  try {
    await restaurantStore.fetchRestaurant(props.restaurantSlug)
    
    // Check for table parameter from QR code
    const tableParam = route.query.table
    if (tableParam) {
      tableNumber.value = tableParam
      tableFromQr.value = true
    }
  } catch (e) {
    restaurantError.value = e.message
  } finally {
    restaurantLoading.value = false
  }
})

const isValidStart = computed(() => {
  if (!tableNumber.value) return false
  if (isCollaborativeStart.value && !userName.value.trim()) return false
  return true
})

const isValidJoin = computed(() => {
  return pinCode.value.length === 4 && userName.value.trim().length > 0
})

const handleStart = async () => {
  if (!isValidStart.value || loading.value) return
  
  loading.value = true
  error.value = ''

  try {
    if (isCollaborativeStart.value) {
      await sessionStore.createCollaborativeSession(
        tableNumber.value, 
        userName.value,
        restaurantStore.restaurantId,
        props.restaurantSlug
      )
    } else {
      await sessionStore.startIndividualSession(
        tableNumber.value,
        restaurantStore.restaurantId,
        props.restaurantSlug
      )
    }
    router.push(`/${props.restaurantSlug}/menu`)
  } catch (e) {
    error.value = e.message
  } finally {
    loading.value = false
  }
}

const handleJoin = async () => {
  if (!isValidJoin.value || loading.value) return

  loading.value = true
  error.value = ''

  try {
    await sessionStore.joinSession(pinCode.value, userName.value, props.restaurantSlug)
    router.push(`/${props.restaurantSlug}/menu`)
  } catch (e) {
    error.value = e.message || 'Failed to join session'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.landing-page {
  padding: 2rem 1.5rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #F2F4F6 0%, #E2E8F0 100%);
}

.hero-section {
  text-align: center;
  margin-bottom: 2.5rem;
  animation: fadeIn 0.8s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-emoji {
  font-size: 4rem;
  margin-bottom: 0.5rem;
  display: inline-block;
  animation: bounce 2s infinite;
}

h1 {
  font-size: 2.5rem;
  color: var(--secondary-color);
  margin-bottom: 0.25rem;
  letter-spacing: -1px;
}

.subtitle {
  color: var(--text-light);
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.lang-toggle {
  background: white;
  border: 1px solid var(--border-color);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--secondary-color);
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: var(--shadow-sm);
}

.lang-toggle:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.card-container {
  background: white;
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 400px;
}

.tabs {
  display: flex;
  position: relative;
  background: #F1F5F9;
  border-radius: 1rem;
  padding: 4px;
  margin-bottom: 2rem;
}

.tab-btn {
  flex: 1;
  padding: 12px;
  z-index: 2;
  font-weight: 600;
  color: var(--text-light);
  transition: color 0.3s;
  background: transparent;
}

.tab-btn.active {
  color: var(--secondary-color);
}

.tab-indicator {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background: white;
  border-radius: 0.8rem;
  box-shadow: var(--shadow-sm);
  transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  z-index: 1;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--secondary-color);
  margin-left: 4px;
}

.pin-field {
  letter-spacing: 0.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
}

.mode-toggle-container {
  display: flex;
  justify-content: flex-start;
}

.mode-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 0;
}

.toggle-track {
  width: 48px;
  height: 28px;
  background: #CBD5E1;
  border-radius: 14px;
  position: relative;
  transition: background 0.3s;
}

.toggle-track.active {
  background: var(--primary-color);
}

.toggle-thumb {
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: var(--shadow-sm);
}

.toggle-track.active .toggle-thumb {
  transform: translateX(20px);
}

.toggle-label {
  font-weight: 600;
  color: var(--secondary-color);
}

.hint {
  font-size: 0.85rem;
  color: var(--text-light);
  margin-left: 4px;
}

.table-hint {
  color: var(--success-color, #10b981);
}

.error-message {
  color: var(--error-color);
  text-align: center;
  font-size: 0.9rem;
  background: #FEF2F2;
  padding: 10px;
  border-radius: var(--radius-sm);
}

/* Loading and Error States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
}

.spinner-lg {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.error-state .icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-state h2 {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.error-state p {
  color: var(--text-secondary);
}

/* Restaurant Logo */
.restaurant-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 16px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>

